/**
 * Cards, for reading at a glance.
 *
 * In the seaport a hand is worth what its sets are worth — three of a kind,
 * four different, pairs — so a hand is shown grouped by commodity with a count,
 * not as a row of twelve chips. The same grouping names what was shipped or
 * displayed in the log, so the two always agree.
 */
import { MANIFESTS, type CommodityCard } from '@trajan/engine';

export interface CardGroup {
  type: string;
  count: number;
}

/** Most first, then alphabetically, so the biggest set is the first thing you read. */
export function groupCards(cards: readonly Pick<CommodityCard, 'type'>[]): CardGroup[] {
  const counts = new Map<string, number>();
  for (const c of cards) counts.set(c.type, (counts.get(c.type) ?? 0) + 1);
  return [...counts.entries()]
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));
}

/**
 * A card's commodity from its id. The id's spelling is the engine's business,
 * so this asks the manifest rather than taking the id apart.
 */
export function cardType(id: string): string {
  return MANIFESTS.tiles.commodity.find((c) => c.id === id)?.type ?? 'card';
}

/** "3 wheat", "wheat and fish", "wheat, fish and cow" — for a sentence in the log. */
export function describeCards(ids: readonly string[]): string {
  const groups = groupCards(ids.map((id) => ({ type: cardType(id) })));
  const parts = groups.map((g) => (g.count > 1 ? `${g.count} ${g.type}` : g.type));
  if (parts.length <= 1) return parts[0] ?? 'nothing';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
